import {useEffect, useRef, useState} from "react";
import {JSONParser} from "@streamparser/json";
import type {ParsedElementInfo} from "@streamparser/json/utils/types/parsedElementInfo.js";
import {useAppStore} from "../../../StateManager";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import type {OrgSearchResponseDto} from "../../../generated/api/search";
import type {IModel} from "../../../data/models/Model";
import {notify} from "../../../utils/Notify";
import {NotificationTitleStyled, NotificationType} from "../popup/NotificationV5";
import {Spinner} from "../loader/Spinner";
import {Flex} from "../styles/styles";
import type {INotificationElementParams} from "../../notification/AppNotifications";
import type {ScopeWideSearchType} from "./OrganizationWideSearchUtils";
import {createLocalModelFromOrgSearchResponse} from "./OrganizationWideSearchUtils";
import {ScopeWideSearchV5} from "./ScopeWideSearchV5";

interface ISearchParams {
	searchTerm: string;
	portfolioID?: string;
}

interface IOrganizationWideSearchProps {
	readonly scopeWideSearchType: ScopeWideSearchType;
	readonly queryString: string;
	readonly closeScopeWideSearch: () => void;
}

export const OrganizationWideSearchV5 = (props: IOrganizationWideSearchProps) => {
	const {queryString, closeScopeWideSearch, scopeWideSearchType} = props;
	const appState = useAppStore((state) => state.appState);
	const [results, setResults] = useState<IModel[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const aborterRef = useRef<AbortController>(new AbortController());

	useEffect(() => {
		const abort = () => {
			aborterRef.current.abort();
		};

		abort();

		let notificationMaybe: INotificationElementParams | null = null;

		if (queryString) {
			setIsLoading(true);

			notificationMaybe = notify(appState.app.notificationContainer, {
				title: (
					<Flex $gap="8px">
						<NotificationTitleStyled>Loading Results</NotificationTitleStyled>
						<Spinner isVisible={true} />
					</Flex>
				),
				isMiniNotification: true,
				isIconVisible: false,
				type: NotificationType.Message,
				cancelable: false,
				lifeTime: Infinity,
				buttonLabel: "Cancel Search",
				onActionButtonClick: () => {
					abort();
					notificationMaybe.onClose();
					setIsLoading(false);
				},
			});

			const readData = async () => {
				abort();
				const jsonParser = new JSONParser();
				jsonParser.onValue = (parsedElementInfo: ParsedElementInfo) => {
					const {value} = parsedElementInfo;
					if (typeof value === "object" && value) {
						if (value.hasOwnProperty("feature") && value.hasOwnProperty("id")) {
							const dto = value as OrgSearchResponseDto;
							const model = createLocalModelFromOrgSearchResponse(appState, dto);

							setResults((prev) => [...prev, model]);
						}
					}
				};

				aborterRef.current = new AbortController();
				const signal = aborterRef.current.signal;
				const params: ISearchParams = {
					searchTerm: queryString,
				};

				if (scopeWideSearchType === "Portfolio" && appState.activePortfolioId) {
					params.portfolioID = appState.activePortfolioId;
				}

				const queryParams = XHRLoader.encodeParams(params as unknown as Record<string, string | string[]>);

				setResults([]);

				const transport = appState.app.transport;
				const authData = transport.services.auth.authData;

				const response = await fetch(`${transport.config.api.search}/search?${queryParams}`, {
					signal,
					method: XHRLoader.METHOD_GET,
					headers: {
						Authorization: `${StringUtils.capitalize(authData.tokenType)} ${authData.accessToken}`,
					},
				});

				const reader = response.body.getReader();
				while (true) {
					if (signal.aborted) {
						break;
					}

					const {done, value} = await reader.read();

					if (done) {
						break;
					}

					jsonParser.write(value);
				}
			};

			readData()
				.catch((error) => {
					if (error.name === "AbortError") {
						console.log("Search aborted");
					} else {
						console.error("Error reading data:", error);
					}
				})
				.finally(() => {
					notificationMaybe?.onClose();
					setIsLoading(false);
				});
		}

		return () => {
			abort();
			setResults([]);
			notificationMaybe?.onClose();
			setIsLoading(false);
		};
	}, [queryString, scopeWideSearchType, appState]);

	return (
		<ScopeWideSearchV5
			closeScopeWideSearch={closeScopeWideSearch}
			scopeWideSearchType={scopeWideSearchType}
			queryString={queryString}
			results={results}
			isLoading={isLoading}
		/>
	);
};
